import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { formatPrice } from '../utils/formatPrice';
import DealConfirmationButton from './DealConfirmationButton';
import BuySellLetterButtons from './BuySellLetterButtons';
import { resolveBuybackSellerBuyer } from '../utils/buybackDeal';

const actionButtonStyle = { padding: '6px 14px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' };

const pagerButtonStyle = (disabled) => ({
  padding: '6px 14px',
  borderRadius: 4,
  border: '1px solid #ccc',
  background: disabled ? '#f1f3f7' : '#fff',
  color: disabled ? '#999' : '#333',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 13,
  lineHeight: 1.4
});

/**
 * Approved buyback deals with settlement letter and confirmation actions.
 * Shared by the back office verifier and final authorizer buyback blotters so both
 * stages see the same letters.
 */
const BuybackApprovedLettersPanel = ({ transactions, onView, pageSize = 50, maxItems = 100 }) => {
  const [page, setPage] = useState(1);

  const approved = useMemo(
    () => (transactions || []).filter((tx) => tx.deal_status === 'Approved').slice(0, maxItems),
    [transactions, maxItems]
  );

  const total = approved.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamped rather than reset via an effect so a shrinking list cannot strand the
  // user on a page that no longer exists.
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageItems = approved.slice(startIndex, startIndex + pageSize);

  if (!total) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: '1.25rem', marginBottom: 8 }}>Approved — Settlement Letters &amp; Confirmations</h3>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
        Settlement letters and deal confirmations can be generated once a buyback deal has been fully
        approved ({total} deal{total === 1 ? '' : 's'}).
        {totalPages > 1 && ` Showing ${startIndex + 1}–${startIndex + pageItems.length} on page ${currentPage} of ${totalPages}.`}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f1f3f7' }}>
            <tr>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Deal Number</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Trade Date</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>ISIN</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Seller</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Buyer</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Face Value</th>
              <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((tx) => {
              const { seller, buyer } = resolveBuybackSellerBuyer(tx);
              return (
                <tr key={tx.id}>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>
                    <strong style={{ color: '#007bff' }}>{tx.deal_number}</strong>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>
                    {tx.leg1_trade_date ? format(new Date(tx.leg1_trade_date), 'yyyy-MM-dd') : ''}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{tx.leg1_isin}</span>
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>{seller}</td>
                  <td style={{ padding: '8px', border: '1px solid #eee' }}>{buyer}</td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                    {formatPrice(tx.leg1_adjusted_face_value ?? tx.leg1_face_value)}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {onView && (
                        <button
                          type="button"
                          onClick={() => onView(tx)}
                          style={{ ...actionButtonStyle, background: '#007bff', color: '#fff' }}
                        >
                          View
                        </button>
                      )}
                      <DealConfirmationButton
                        dealType="buyback"
                        dealId={tx.id}
                        refNumberHint={tx.deal_number}
                        className=""
                        style={{ ...actionButtonStyle, background: '#6c757d', color: '#fff' }}
                      />
                      <BuySellLetterButtons tx={tx} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage <= 1}
            style={pagerButtonStyle(currentPage <= 1)}
          >
            Previous
          </button>
          <span style={{ color: '#666', fontSize: 14 }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            style={pagerButtonStyle(currentPage >= totalPages)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BuybackApprovedLettersPanel;
